import axios from '@/lib/axios'
import { buildAdminRequestConfig, type ScopedAdminQueryKey } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  AvailabilityCalendarFilters,
  AvailabilityCalendarOccurrence,
  AvailabilityChange,
  AvailabilityChangeIndexFilters,
  AvailabilityChangeInput,
  AvailabilityCurrentSummary,
} from './availability-changes.types'

/** Every availability change request and scoped query key carries the organization. */
export interface AvailabilityChangeOrganizationScope {
  organizationId: number
}

export type AvailabilityChangeIndexQueryKeyEntries = AvailabilityChangeOrganizationScope & {
  filters?: AvailabilityChangeIndexFilters | undefined
}

export type AvailabilityChangeCalendarQueryKeyEntries = AvailabilityChangeOrganizationScope & {
  filters?: AvailabilityCalendarFilters | undefined
}

/** Scope for calls that act on one availability change. */
interface AvailabilityChangeTarget extends AvailabilityChangeOrganizationScope {
  availabilityChangeId: number
}

/** Query functions read only the key. */
type AvailabilityChangeQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

export async function getAvailabilityChanges({
  queryKey: [{ organizationId, filters = {} }],
}: AvailabilityChangeQueryContext<AvailabilityChangeIndexQueryKeyEntries>) {
  return (
    (await axios.get<AvailabilityChange[]>(
      '/availability-changes',
      buildAdminRequestConfig({ organizationId, params: { ...filters } })
    )) || []
  )
}

export async function getAvailabilityChangeCalendar({
  queryKey: [{ organizationId, filters }],
}: AvailabilityChangeQueryContext<AvailabilityChangeCalendarQueryKeyEntries>) {
  return (
    (await axios.get<AvailabilityCalendarOccurrence[]>(
      '/availability-changes/calendar',
      buildAdminRequestConfig({ organizationId, params: { ...filters } })
    )) || []
  )
}

export async function getAvailabilityChangeCurrentSummary({
  queryKey: [{ organizationId }],
}: Pick<QueryFunctionContext<ScopedAdminQueryKey>, 'queryKey'>) {
  return (
    (await axios.get<AvailabilityCurrentSummary>(
      '/availability-changes/current-summary',
      buildAdminRequestConfig({ organizationId })
    )) || {}
  )
}

export async function createAvailabilityChange({
  organizationId,
  ...values
}: AvailabilityChangeOrganizationScope & AvailabilityChangeInput) {
  return axios.post<AvailabilityChange>(
    '/availability-changes',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateAvailabilityChange({
  organizationId,
  availabilityChangeId,
  ...values
}: AvailabilityChangeTarget & AvailabilityChangeInput & { id?: number }) {
  return axios.put<AvailabilityChange>(
    `/availability-changes/${availabilityChangeId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function softDeleteAvailabilityChange({
  organizationId,
  availabilityChangeId,
}: AvailabilityChangeTarget) {
  return axios.delete<void>(
    `/availability-changes/${availabilityChangeId}`,
    buildAdminRequestConfig({ organizationId })
  )
}
